'use client';

import { Cancel01Icon } from '@terra/ui-icons';
import { HStack, VStack } from '@terra/ui-styled-system';
import { useTranslations } from 'next-intl';
import { Button } from '../../components/button';
import { LegacyDialog } from '../../components/dialog';
import { IconButton } from '../../components/icon-button';

export type WizardActionDialogProps = {
  open: boolean;
  title: string;
  description: string;
  action: string;
  onOpenChange: (open: boolean) => void;
  onActionClick: () => void | Promise<void>;
  cancelText?: string;
  secondaryAction?: string;
  onSecondaryActionClick?: () => void | Promise<void>;
  loading?: boolean;
  secondaryLoading?: boolean;
  actionColor?: string;
};

export const WizardActionDialog = ({
  open,
  title,
  description,
  action,
  onOpenChange,
  onActionClick,
  cancelText,
  secondaryAction,
  onSecondaryActionClick,
  loading,
  secondaryLoading,
  actionColor,
}: WizardActionDialogProps) => {
  const t = useTranslations('designSystem.confirmationDialog');
  const resolvedCancelText = cancelText ?? t('cancel');
  const hasSecondaryAction = Boolean(secondaryAction && onSecondaryActionClick);
  const isBusy = Boolean(loading || secondaryLoading);
  const actionButtonStyle = actionColor
    ? {
        backgroundColor: `var(--colors-${actionColor.replaceAll('.', '-')})`,
        color: 'var(--colors-fg-onaccent-default)',
      }
    : undefined;

  return (
    <LegacyDialog.Root
      open={open}
      onOpenChange={({ open: nextOpen }) => onOpenChange(nextOpen)}
    >
      <LegacyDialog.Backdrop />
      <LegacyDialog.Positioner>
        <LegacyDialog.Content maxWidth="480px" position="relative">
          <LegacyDialog.CloseTrigger asChild>
            <IconButton
              variant="ghost"
              size="sm"
              aria-label={t('close')}
              disabled={isBusy}
              position="absolute"
              top={4}
              right={4}
            >
              <Cancel01Icon size={16} />
            </IconButton>
          </LegacyDialog.CloseTrigger>
          <VStack gap={6} p={6} alignItems="stretch">
            <VStack gap={2} alignItems="flex-start" pr={8}>
              <LegacyDialog.Title
                fontSize="18px"
                fontWeight="medium"
                lineHeight="tight"
              >
                {title}
              </LegacyDialog.Title>
              <LegacyDialog.Description
                color="text.muted"
                fontSize="sm"
                lineHeight="1.5"
              >
                {description}
              </LegacyDialog.Description>
            </VStack>
            <HStack
              gap={3}
              justifyContent={hasSecondaryAction ? 'space-between' : 'flex-end'}
              alignItems="center"
              w="full"
            >
              <Button
                variant={hasSecondaryAction ? 'ghost' : 'outline'}
                onClick={() => onOpenChange(false)}
                disabled={isBusy}
              >
                {resolvedCancelText}
              </Button>
              <HStack gap={3}>
                {hasSecondaryAction ? (
                  <Button
                    variant="outline"
                    onClick={onSecondaryActionClick}
                    loading={secondaryLoading}
                    disabled={isBusy}
                  >
                    {secondaryAction}
                  </Button>
                ) : null}
                <Button
                  style={actionButtonStyle}
                  onClick={onActionClick}
                  loading={loading}
                  disabled={isBusy}
                >
                  {action}
                </Button>
              </HStack>
            </HStack>
          </VStack>
        </LegacyDialog.Content>
      </LegacyDialog.Positioner>
    </LegacyDialog.Root>
  );
};
